/**
 * Bloc « Progression » côté prof : pour chaque exercice que l'élève refait
 * souvent (≥ 2 tentatives terminées), une courbe de ses scores dans le temps +
 * une tendance. Rendu SVG fait main (aucune lib de charts dans le repo). Se
 * masque tout seul (return null) quand il n'y a rien à montrer.
 */
import { useEffect, useRef, useState } from 'react';
import { TrendingUp, TrendingDown, Minus, LineChart } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  sparklinePoints,
  type ExerciseProgress,
  type ProgressTrend,
} from '@/lib/tutor/student-progress';

/** Nombre d'exercices affichés (les plus refaits en premier). */
const MAX_SHOWN = 4;

const TREND_META: Record<
  ProgressTrend,
  { icon: typeof TrendingUp; color: string; bg: string; label: string }
> = {
  up: { icon: TrendingUp, color: 'text-xp-600', bg: 'bg-xp-50', label: 'En progrès' },
  down: { icon: TrendingDown, color: 'text-error-600', bg: 'bg-error-50', label: 'En baisse' },
  flat: { icon: Minus, color: 'text-ink-500', bg: 'bg-ink-100', label: 'Stable' },
};

/** Largeur de repli avant la 1re mesure (évite un flash étroit au montage). */
const CHART_FALLBACK_W = 640;
const CHART_H = 72;
const CHART_PAD = 8;
/** Seuil de réussite (%) tracé en pointillés — repère de lecture. */
const PASS_LINE = 60;

/**
 * Largeur en pixels d'un élément, suivie via `ResizeObserver`. Permet de dessiner
 * le SVG à sa vraie largeur (viewBox = largeur mesurée) → la courbe remplit toute
 * la carte sans le letterboxing du `preserveAspectRatio` par défaut, et les points
 * restent parfaitement ronds (aucune distorsion).
 */
function useMeasuredWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

function ScoreTrendChart({ progress }: { progress: ExerciseProgress }) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>(CHART_FALLBACK_W);
  const scores = progress.points.map((p) => p.score);
  const pts = sparklinePoints(scores, width, CHART_H, CHART_PAD);
  const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPts = [
    `${CHART_PAD},${CHART_H - CHART_PAD}`,
    line,
    `${width - CHART_PAD},${CHART_H - CHART_PAD}`,
  ].join(' ');
  const passY = CHART_PAD + (1 - PASS_LINE / 100) * (CHART_H - CHART_PAD * 2);

  return (
    <div ref={ref} className="mt-3">
      <svg
        viewBox={`0 0 ${width} ${CHART_H}`}
        className="text-primary-400 block w-full"
        style={{ height: CHART_H }}
        role="img"
        aria-label={`Évolution des scores sur ${progress.attemptCount} tentatives`}
      >
        {/* Repère seuil de réussite (60%) */}
        <line
          x1={CHART_PAD}
          x2={width - CHART_PAD}
          y1={passY}
          y2={passY}
          className="stroke-ink-200"
          strokeWidth={1}
          strokeDasharray="4 4"
        />
        {/* Aire sous la courbe */}
        <polyline points={areaPts} className="fill-primary-50" stroke="none" />
        {/* Courbe */}
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Points */}
        {pts.map((p, i) => (
          <circle
            key={progress.points[i]?.attemptId ?? i}
            cx={p.x}
            cy={p.y}
            r={2.5}
            className={cn(
              'stroke-white',
              i === pts.length - 1 ? 'fill-primary-600' : 'fill-primary-400',
            )}
            strokeWidth={1}
          />
        ))}
      </svg>
    </div>
  );
}

function ProgressCard({ progress }: { progress: ExerciseProgress }) {
  const meta = TREND_META[progress.trend];
  const TrendIcon = meta.icon;
  const deltaLabel =
    progress.delta > 0 ? `+${progress.delta}` : progress.delta < 0 ? `${progress.delta}` : '±0';

  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="font-display text-ink-900 min-w-0 flex-1 truncate text-sm font-bold">
          {progress.exerciseTitle}
        </p>
        <span
          className={cn(
            'font-display inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold',
            meta.bg,
            meta.color,
          )}
        >
          <TrendIcon className="h-3 w-3" />
          {meta.label}
        </span>
      </div>

      <ScoreTrendChart progress={progress} />

      <div className="text-ink-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-xs">
        <span>
          <span className="text-ink-700 font-mono font-semibold">{progress.attemptCount}</span>{' '}
          tentatives
        </span>
        <span className="text-ink-300">·</span>
        <span>
          <span className="font-mono">{progress.firstScore}%</span> →{' '}
          <span className="text-ink-900 font-mono font-semibold">{progress.lastScore}%</span>{' '}
          <span className={cn('font-mono font-semibold', meta.color)}>({deltaLabel})</span>
        </span>
        <span className="text-ink-300">·</span>
        <span>
          Meilleur{' '}
          <span className="text-xp-600 font-mono font-semibold">{progress.bestScore}%</span>
        </span>
      </div>
    </div>
  );
}

export function StudentProgressBlock({ progress }: { progress: ExerciseProgress[] }) {
  if (progress.length === 0) return null;
  const shown = progress.slice(0, MAX_SHOWN);
  const hiddenCount = progress.length - shown.length;

  return (
    <section className="mb-6">
      <h2 className="font-display text-ink-500 mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest">
        <LineChart className="h-3.5 w-3.5" />
        Progression sur les exercices refaits
      </h2>
      <div className="space-y-3">
        {shown.map((p) => (
          <ProgressCard key={p.exerciseId} progress={p} />
        ))}
      </div>
      {hiddenCount > 0 && (
        <p className="text-ink-400 mt-2 font-sans text-xs">
          + {hiddenCount} autre{hiddenCount > 1 ? 's' : ''} exercice{hiddenCount > 1 ? 's' : ''}{' '}
          refait{hiddenCount > 1 ? 's' : ''}.
        </p>
      )}
    </section>
  );
}
